import React, { useEffect, useState } from 'react';
import {
  Row, Col,
} from 'react-bootstrap';
import { store } from 'redux/store';
import { useSelector } from 'react-redux';
import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import { setCurrentTab } from 'redux/reducers/fluxPersonalizedLayoutReducer';
import EmptyListAlert from 'components/EmptyListAlert';
import Accordion from 'components/Accordion';
import Checkbox from 'components/DesignSystem/Checkbox';
import { ResponseError } from 'services/api/base';
import { useListPersonalizedStudiesQuery } from 'services/api/personalized';
import { useHandleRequestErrors } from 'utils/hooks';
import PersonalizedRow from 'components/Flux/Personalized/Row';
import PersonalizedStudyCard from 'components/Flux/Personalized/Mobile/StudyCard';
import MyStudies from 'components/Flux/Personalized/MyStudies';
import featureFlags from 'constants/featureFlags';
import { checkDataCompletion, getDataLength } from 'helper/scrollStopper';
import useInfiniteScrollPaging from 'utils/useInfiniteScrollPaging';
import useListEnd from 'utils/hooks/useListEnd';
import useIsMobile from 'utils/hooks/useIsMobile';
import 'views/Flux/Upload/index.scss';
import './index.scss';

export default function Personalized() {
  const isAdmin: boolean = useSelector(selectUserIsAdmin);
  const isMobile = useIsMobile();
  // Nova Meus Estudos só no desktop e atrás da flag; o mobile segue com os cards legados.
  const showNewDesktop = featureFlags.fluxMeusEstudosV2 && !isMobile;
  const [ampereCases, setAmpereCases] = useState<boolean>(isAdmin);
  const [maxLength, setMaxLength] = useState<number>(-1);
  const pageSize = 12;
  const { page } = useInfiniteScrollPaging();
  const pageLength = pageSize * page;
  const dataLength = getDataLength(maxLength, pageLength);

  const { data: personalizedStudies, isFetching, error } = useListPersonalizedStudiesQuery({
    ampereCases,
    page: 1,
    pageSize: dataLength,
  }, { skip: showNewDesktop });

  const { listEnded } = useListEnd(personalizedStudies, page, pageSize, isFetching);

  useHandleRequestErrors(error as ResponseError | undefined);

  useEffect(() => {
    store.dispatch(setCurrentTab('list'));
  });

  useEffect(() => {
    if (checkDataCompletion(dataLength, isFetching, personalizedStudies)) {
      setMaxLength(dataLength);
    }
  }, [dataLength, personalizedStudies, isFetching]);

  const studies = personalizedStudies?.map((study) => ({
    study,
    key: study.id,
  })) ?? [];

  // Depois de todos os hooks, para a ordem deles não mudar ao cruzar o breakpoint.
  if (showNewDesktop) {
    return <MyStudies />;
  }

  if (isMobile) {
    const resultCount = personalizedStudies?.length ?? 0;

    return (
      <div className="upload-list-mobile">
        <div className="upload-list-mobile-toolbar">
          <span className="upload-list-mobile-count">
            {resultCount}
            {resultCount === 1 ? ' estudo' : ' estudos'}
          </span>
          {isAdmin && (
            <Checkbox
              label="Casos Ampere"
              checked={ampereCases}
              onChange={() => { setAmpereCases(!ampereCases); }}
            />
          )}
        </div>
        <div className="upload-list-mobile-cards">
          <EmptyListAlert
            message="Não foi encontrado nenhum estudo"
            array={studies}
            component={PersonalizedStudyCard}
            isFetching={isFetching}
            listEnded={listEnded}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="list-container">
      {isAdmin && (
        <div className="d-flex justify-content-end">
          <Checkbox
            className="me-5 mb-1"
            label="Casos Ampere"
            checked={ampereCases}
            onChange={() => { setAmpereCases(!ampereCases); }}
          />
        </div>
      )}
      <Row className="flux-personalized-list-header">
        <Col md={4} className="special-header">
          DESCRIÇÃO
        </Col>
        <Col md={4} className="special-header">
          HORIZONTE
        </Col>
        <Col md={4} className="special-header text-black">
          STATUS
        </Col>
      </Row>
      <Accordion>
        <EmptyListAlert
          message="Não foi encontrado nenhum estudo"
          array={studies}
          component={PersonalizedRow}
          isFetching={isFetching}
          listEnded={listEnded}
        />
      </Accordion>
    </div>
  );
}
